/**
 * 分拣批次 mock 写操作的「计划」函数（纯函数）：根据当前 mock 状态与用户输入算出要应用的变更，
 * 由页面 dispatch 到 reducer 应用。逐函数转写自原型 createBatch / applyBags / createAndPrint /
 * moveCreateBag / runMoveIn / runMoveOut。
 *
 * todo batch mock: 待后端提供 Mutation createSortingBatches / createSortingBatchBags /
 * createSortingBatchBagForMove / moveIntoSortingBatch / moveOutOfSortingBatch，接入后删除本文件。
 */
import { BagStatus } from '../../bags/graphql/types/BagTagType';
import {
    BagMove, BatchBag, BatchLog, MoveInPreview, MoveInResultRow, MoveInSelections, MoveOutItem, ParcelMoveIn, PrintJob, SortingBatch,
} from '../types';
import { buildBatchNumber, parseDestinationKey } from '../utils/batchNameUtils';
import { MOCK_OPERATORS } from './batchMock';
import {
    currentBatchOfNumber, findKnownBag, groupParcelsByCode, makeBagNumber, MoveSource, moveBagsForCode,
} from './batchMockDerive';
import { movedOutKey, SortingBatchMockState } from './mockState';

/** 移入时「新建包牌」选项的占位值 */
export const NEW_BAG_TOKEN = '__new';

const operatorInfo = (state: SortingBatchMockState) => ({
    operator: MOCK_OPERATORS.hubManager,
    operatorRole: 'HUB_MANAGER' as const,
    site: `${state.hubPrefix} Hub`,
});

// ---------------------------------------------------------------- 新建批次

/** 勾选「同流向分拣中批次结束分拣」时会被同步结束的批次（原型 endSameTargets：仅分拣中） */
export const endSameFlowTargets = (state: SortingBatchMockState, destinationKeys: string[]): SortingBatch[] => {
    const flows = destinationKeys.map((k) => parseDestinationKey(k).destination);
    return state.outbound.filter((b) => b.status === 'SORTING' && flows.includes(b.destination));
};

export interface CreateBatchesPlan {
    batches: SortingBatch[];
    endIds: number[];
    logs: BatchLog[];
}

export const planCreateBatches = (
    state: SortingBatchMockState,
    input: { destinationKeys: string[]; batchTime: number; endSameFlow: boolean; creator: string; now: number },
): CreateBatchesPlan => {
    const batches: SortingBatch[] = input.destinationKeys.map((key, i) => {
        const { destination } = parseDestinationKey(key);
        return {
            id: state.nextBatchId + i,
            batchNumber: buildBatchNumber(state.hubPrefix, input.batchTime, destination),
            destination,
            creationSite: state.hubPrefix,
            status: 'CREATED',
            creationMethod: 'MANUAL',
            creator: input.creator,
            packedBags: 0,
            createdBags: 0,
            parcels: 0,
            createdAt: input.batchTime,
            isNew: true,
        };
    });
    const ends = input.endSameFlow ? endSameFlowTargets(state, input.destinationKeys) : [];
    const logs: BatchLog[] = ends.map((b) => ({
        batchNumber: b.batchNumber,
        type: 'SORTING_ENDED',
        time: input.now,
        syncByBatch: batches.find((n) => n.destination === b.destination)?.batchNumber,
        ...operatorInfo(state),
    }));
    return { batches, endIds: ends.map((b) => b.id), logs };
};

// ---------------------------------------------------------------- 创建包牌

/**
 * 为批次生成新包牌（未使用）：编号接在批次现有包牌之后。
 * @param codes 已按「分拣码 × 份数」展开的码序列
 */
export const planNewBags = (batch: SortingBatch, codes: string[], options?: { createdForMove?: boolean; offset?: number }): BatchBag[] =>
    codes.map((code, k) => {
        const index = batch.createdBags + (options?.offset || 0) + k;
        return {
            bagNumber: makeBagNumber(batch.id, index),
            sortingCode: code,
            status: BagStatus.NOT_USED,
            creationMethod: 'MANUAL',
            parcels: 0,
            batchNumber: batch.batchNumber,
            createdForMove: options?.createdForMove,
        };
    });

export const toPrintJobs = (bags: BatchBag[], batch: SortingBatch): PrintJob[] =>
    bags.map((g) => ({
        bagId: g.bagId,
        bagNumber: g.bagNumber,
        sortingCode: g.sortingCode,
        status: g.status,
        batchNumber: g.batchNumber || batch.batchNumber,
        // 批次详情抽屉打印：标签批次段取正在查看的这个批次（和批次列表打印同口径），不走物理当前批次
        batchId: batch.id != null ? String(batch.id) : undefined,
        destination: batch.destination,
        copies: 1,
    }));

// ---------------------------------------------------------------- 移入

export type CreateBagForMoveResult = { bag: BatchBag } | { error: 'NO_CODE' | 'CODE_MISMATCH' };

/** 移入时在目标批次新建包牌（原型 moveCreateBag）：分拣码必须属于包裹目的地 */
export const planCreateBagForMove = (
    state: SortingBatchMockState,
    batch: SortingBatch,
    destination: string,
    code: string,
): CreateBagForMoveResult => {
    if (!code) return { error: 'NO_CODE' };
    if (String(code).indexOf(destination) !== 0) return { error: 'CODE_MISMATCH' };
    return { bag: planNewBags(batch, [code], { createdForMove: true })[0] };
};

export interface MovePlan {
    logs: BatchLog[];
    bagMoves: Record<string, BagMove[]>;
    movedOutKeys: string[];
    movedInBags: Record<number, BatchBag[]>;
    parcelMoveIns: Record<string, ParcelMoveIn[]>;
    removedNumbers: string[];
}

const emptyPlan = (): MovePlan => ({ logs: [], bagMoves: {}, movedOutKeys: [], movedInBags: {}, parcelMoveIns: {}, removedNumbers: [] });

const pushTo = <K extends string | number, T>(map: Record<K, T[]>, key: K, value: T) => {
    map[key] = [...(map[key] || []), value];
};

/** 当前选择下包裹组的目标包牌（未选择时默认第一个候选，无候选为新建） */
export const selectedBagFor = (
    state: SortingBatchMockState,
    batch: SortingBatch,
    destination: string,
    code: string,
    selections: MoveInSelections,
): { candidates: BatchBag[]; selected: string } => {
    const candidates = moveBagsForCode(state, batch, destination, code);
    const selected = selections.bagByDestinationCode[`${destination}|${code}`] || (candidates[0] ? candidates[0].bagNumber : NEW_BAG_TOKEN);
    return { candidates, selected };
};

export const selectedBatchFor = (candidates: SortingBatch[], destination: string, selections: MoveInSelections): SortingBatch => {
    const id = selections.batchByDestination[destination];
    return candidates.find((b) => b.id === id) || candidates[0];
};

/** 是否有包裹组的目标包牌仍为「新建包牌…」且未新建：包牌须先创建再移入（plan §10-Q10） */
export const hasPendingNewBag = (state: SortingBatchMockState, preview: MoveInPreview, selections: MoveInSelections): boolean =>
    preview.groups.some((g) => {
        const target = selectedBatchFor(g.candidates, g.destination, selections);
        return groupParcelsByCode(state, target, g.destination, g.items)
            .some((x) => selectedBagFor(state, target, g.destination, x.code, selections).selected === NEW_BAG_TOKEN);
    });

/** 执行移入（原型 runMoveIn）：两侧批次各写日志，记录包牌移动与包裹移入 */
export const planMoveIn = (
    state: SortingBatchMockState,
    preview: MoveInPreview,
    selections: MoveInSelections,
    source: MoveSource | null,
    now: number,
): { plan: MovePlan; rows: MoveInResultRow[] } => {
    const plan = emptyPlan();
    const rows: MoveInResultRow[] = [];
    const op = operatorInfo(state);
    preview.groups.forEach((g) => {
        const target = selectedBatchFor(g.candidates, g.destination, selections);
        g.items.filter((i) => i.isBag).forEach((it) => {
            const from = currentBatchOfNumber(state, it.number, g.destination, target.batchNumber, source);
            plan.logs.push({ batchNumber: target.batchNumber, type: 'BAG_MOVED_IN', time: now, bagNumber: it.number, sourceBatch: from, ...op });
            if (from) {
                plan.logs.push({ batchNumber: from, type: 'BAG_MOVED_OUT', time: now, bagNumber: it.number, targetBatch: target.batchNumber, ...op });
                plan.movedOutKeys.push(movedOutKey(from, it.number));
            }
            pushTo(plan.bagMoves, it.number, { from: from || '', to: target.batchNumber, time: now });
            const known = findKnownBag(state, it.number);
            pushTo(plan.movedInBags, target.id, {
                bagNumber: it.number,
                sortingCode: known ? known.bag.sortingCode : g.destination,
                status: known ? known.bag.status : BagStatus.USED,
                creationMethod: known ? known.bag.creationMethod : 'MANUAL',
                parcels: known ? known.bag.parcels : 0,
                batchNumber: target.batchNumber,
            });
            rows.push({ number: it.number, bag: it.number, batchNumber: target.batchNumber, result: 'MOVED_IN_REPRINT' });
        });
        groupParcelsByCode(state, target, g.destination, g.items).forEach((x) => {
            const { candidates, selected } = selectedBagFor(state, target, g.destination, x.code, selections);
            const bag = candidates.find((c) => c.bagNumber === selected);
            const froms = new Map<string, string[]>();
            x.numbers.forEach((n) => {
                const f = currentBatchOfNumber(state, n, g.destination, target.batchNumber, source);
                if (f) froms.set(f, [...(froms.get(f) || []), n]);
            });
            plan.logs.push({
                batchNumber: target.batchNumber, type: 'PARCEL_MOVED_IN', time: now,
                parcelNumber: x.numbers[0], parcelCount: x.numbers.length, targetBagNumber: bag?.bagNumber, ...op,
            });
            froms.forEach((nos, f) => plan.logs.push({
                batchNumber: f, type: 'PARCEL_MOVED_OUT', time: now, parcelNumber: nos[0], parcelCount: nos.length, targetBatch: target.batchNumber, ...op,
            }));
            if (bag) {
                pushTo(plan.parcelMoveIns, bag.bagNumber, { numbers: x.numbers, batchNumber: target.batchNumber, time: now, from: Array.from(froms.keys())[0] });
            }
            x.numbers.forEach((n) => {
                let result: MoveInResultRow['result'] = 'MOVED_IN_NEW_BAG_CREATED';
                if (bag) result = bag.createdForMove ? 'MOVED_IN_NEW_BAG_PENDING_PRINT' : 'MOVED_IN';
                rows.push({ number: n, bag: bag?.bagNumber, bagIsNew: !bag || !!bag.createdForMove, batchNumber: target.batchNumber, result });
            });
        });
    });
    preview.orphans.forEach((o) => rows.push({ number: o.number, result: 'FAILED', reason: o.reason }));
    return { plan, rows };
};

// ---------------------------------------------------------------- 移出

/** 执行移出（原型 runMoveOut）：移出后不属于任何批次 */
export const planMoveOut = (state: SortingBatchMockState, items: MoveOutItem[], now: number): { plan: MovePlan; count: number } => {
    const plan = emptyPlan();
    const op = operatorInfo(state);
    const ok = items.filter((i) => !i.rejectReason && i.currentBatch);
    const byBatch = new Map<string, { bags: string[]; parcels: string[] }>();
    ok.forEach((i) => {
        const key = i.currentBatch as string;
        const entry = byBatch.get(key) || { bags: [], parcels: [] };
        (i.isBag ? entry.bags : entry.parcels).push(i.number);
        byBatch.set(key, entry);
    });
    byBatch.forEach((g, batchNumber) => {
        g.bags.forEach((no) => {
            plan.logs.push({ batchNumber, type: 'BAG_MOVED_OUT', time: now, bagNumber: no, ...op });
            pushTo(plan.bagMoves, no, { from: batchNumber, to: null, time: now });
            plan.movedOutKeys.push(movedOutKey(batchNumber, no));
            plan.removedNumbers.push(no);
        });
        if (g.parcels.length) {
            plan.logs.push({ batchNumber, type: 'PARCEL_MOVED_OUT', time: now, parcelNumber: g.parcels[0], parcelCount: g.parcels.length, ...op });
            g.parcels.forEach((no) => plan.removedNumbers.push(no));
        }
    });
    return { plan, count: ok.length };
};
